// src/screens/question_engine/QuestionEngine.jsx
import React, { Component } from 'react';
import { fireGtmSteps } from '../../utils/dataLayer';
import { Container, Box } from '@mui/material';
import { withRouter } from '../../utils/withRouter';
import QuestionEngineComponent from '../../components/QuestionEngine/QuestionEngineComponent';
import SideProgressBarComponent from '../../components/SideProgressBar/SideProgressBarComponent';
import PopupModalComponent from '../../components/PopupModal/PopupModalComponent';
import { useMobileContext } from '../../contexts/MobileContext';
import SeoTagsManager from '../../utils/SeoTagsManager';
import { getPersonPreFill } from '../../api/apiServices';
import {
  profileSendOTPViaSMS,
  profileSendOTPViaEmail,
  profileValidateOTP,
  profileRetrievePerson,
  profileRetrievePropertyDetails,
  profileRetrievePetDetails,
  profileRetrieveVehicles,
  profileRetrieveVehicleDrivers,
  getCrossSellQuotes,
} from '../../api/apiServices';
import {
  syncAccountData,
  readAccountDataCache,
} from '../../utils/accountDataCache';

import {
  updateFormValuesFromApi,
  synthesizeFallbackLists,
  buildDriversFromSeparateArray,
} from '../../utils/returningUserApiMapper';
import {
  persistCrossSellQuotes,
  unmarkProductChosenForQuestionFlow,
} from '../../utils/carouselQuoteHelpers';
import { getStepperData } from '../../utils/stepperDataFactory';
import SessionManager from '../../utils/sessionManager';

// helpers
import { getRouteMeta } from '../../utils/questionEngineRouteMeta';
import {
  isEmptyPrefill,
  autoFillMissingAddresses,
  transformPrefillResponse,
  stripPrefillPersonValues,
} from '../../utils/questionEnginePrefillHelpers';
import {
  safeParse,
  normalizeCellNumber,
  isValidSaContactNumber,
  buildConfirmSendOtpModal,
  buildEnterOtpModal,
  buildEnterEmailOtpModal,
  buildRequestOtpModal,
  persistAuthSession,
  persistReturnUserDetails,
  isValidatedWithin,
  markOtpStepupShown,
  wasOtpStepupShown,
  markOtpVerifiedForProduct,
  clearPersistedAuthSession,
  ENABLE_OTP_SMS,
} from '../../utils/questionEngineOtpHelpers';
import { getResultsPathForFlow } from '../../utils/questionEngineResultNavigation';
import {
  COMBO_FLOW_CATEGORY,
  COMBO_FLOW_PRODUCT,
  COMBO_FLOW_SLUG,
  COMBO_RESULTS_PATH,
  advanceComboJourney,
  applyComboFieldChange,
  buildComboJourney,
  getComboProjectedValues,
  getCurrentComboQueueItem,
  getNextComboQueueItem,
  getSelectedComboProducts,
  isComboFlowSlug,
  mergeIntoComboFormValues,
  seedComboFlowsFromFormValues,
  setComboJourneyState,
  shouldSkipComboQuestion,
} from '../../utils/comboJourneyHelpers';

// ---------------- Question Engine ----------------
class QuestionEngine extends Component {
  questionNavigationLocked = false;
  navigationDirection = 'forward';

  constructor(props) {
    super(props);
    this.state = {
      currentIndex: 1,
      questionData: null,
      isDone: false,
      formValues: {},
      showModal: false,
      activeModalData: null,
      historyStack: [],
      isLoading: false,

      // OTP state
      otpModalOpen: false,
      otpModalData: null,
      otpStage: 'idle', // 'idle' | 'confirm' | 'request' | 'enter'
      otpSubmitting: false,
      otpError: '',
      otpResetCounter: 0,
      otpViaEmail: false,
    };
  }

  lockQuestionNavigation = () => {
    // Prevent repeated "Next" taps while the next question is still loading.
    this.questionNavigationLocked = true;
    if (!this.state.isLoading) {
      this.setState({ isLoading: true });
    }
  };

  unlockQuestionNavigation = () => {
    this.questionNavigationLocked = false;
    if (this.state.isLoading) {
      this.setState({ isLoading: false });
    }
  };

  // Thin wrapper so we can keep callsites the same
  getRouteMetaFromProps = (props) => getRouteMeta(props);

  isComboFlowActive = (
    props = this.props,
    formValues = this.state.formValues
  ) =>
    isComboFlowSlug(this.getRouteMetaFromProps(props).flowSlug) &&
    (!!formValues ||
      isComboFlowSlug(this.getRouteMetaFromProps(props).flowSlug));

  getProjectedFormValues = (
    formValues = this.state.formValues,
    props = this.props
  ) => {
    const meta = this.getRouteMetaFromProps(props);

    if (!this.isComboFlowActive(props, formValues)) {
      return formValues || {};
    }

    return getComboProjectedValues(formValues || {}, meta.product);
  };

  mergeIntoCurrentFormValues = (
    baseFormValues,
    incomingValues,
    props = this.props
  ) => {
    const meta = this.getRouteMetaFromProps(props);

    if (!this.isComboFlowActive(props, baseFormValues)) {
      return {
        ...(baseFormValues || {}),
        ...(incomingValues || {}),
      };
    }

    return mergeIntoComboFormValues(
      baseFormValues || {},
      meta.product,
      incomingValues || {}
    );
  };

  applyCurrentFormChange = (
    baseFormValues,
    name,
    value,
    props = this.props
  ) => {
    const meta = this.getRouteMetaFromProps(props);

    if (!this.isComboFlowActive(props, baseFormValues)) {
      return {
        ...(baseFormValues || {}),
        [name]: value,
      };
    }

    return applyComboFieldChange(
      baseFormValues || {},
      meta.product,
      name,
      value
    );
  };

  persistFormValues = (formValues) => {
    SessionManager.setFormValues(formValues);
  };

  seedVehiclesFromAccountCache = (formValues) => {
    const cache = readAccountDataCache();
    if (!Array.isArray(cache?.vehicles) || cache.vehicles.length === 0)
      return formValues;
    return { ...formValues, Vehicles: cache.vehicles };
  };

  seedDriversFromAccountCache = (formValues) => {
    const cache = readAccountDataCache();
    if (!Array.isArray(cache?.drivers) || cache.drivers.length === 0)
      return formValues;
    return {
      ...formValues,
      Drivers: buildDriversFromSeparateArray(cache.drivers),
    };
  };

  seedReturningUserListsFromQuote = (formValues) => {
    const hasVehicles =
      Array.isArray(formValues?.Vehicles) && formValues.Vehicles.length > 0;
    const hasDrivers =
      Array.isArray(formValues?.Drivers) && formValues.Drivers.length > 0;
    if (hasVehicles && hasDrivers) return formValues;

    const { vehicles, drivers } = synthesizeFallbackLists(formValues);
    return {
      ...formValues,
      Vehicles: hasVehicles ? formValues.Vehicles : vehicles,
      Drivers: hasDrivers ? formValues.Drivers : drivers,
    };
  };

  navigateToQuestion = ({
    flowSlug,
    category,
    product,
    questionIndex,
    replace = false,
    formValues = this.state.formValues,
  }) => {
    if (!flowSlug) return;

    this.props.navigate(`/${flowSlug}/step${questionIndex}`, {
      replace,
      state: {
        ...this.props.location.state,
        questionIndex,
        formValues,
        category,
        product,
      },
    });
  };

  startComboJourney = () => {
    const projectedFormValues = this.getProjectedFormValues();
    const comboJourney = buildComboJourney(projectedFormValues.comboSelection);

    if (!comboJourney.queue.length) return;

    const seededFormValues = seedComboFlowsFromFormValues(
      this.state.formValues,
      comboJourney.selectedProducts
    );
    const nextFormValues = setComboJourneyState(seededFormValues, comboJourney);
    const firstFlow = getCurrentComboQueueItem(nextFormValues);

    this.persistFormValues(nextFormValues);

    this.setState({ formValues: nextFormValues }, () => {
      if (!firstFlow) return;
      this.switchFlow(firstFlow.product, 1, {
        category: firstFlow.category,
      });
    });
  };

  advanceComboJourneyToNextFlow = () => {
    const nextFlow = getNextComboQueueItem(this.state.formValues);
    if (!nextFlow) return false;

    const nextFormValues = advanceComboJourney(this.state.formValues);
    this.persistFormValues(nextFormValues);

    this.setState({ formValues: nextFormValues }, () => {
      this.switchFlow(nextFlow.product, 1, {
        category: nextFlow.category,
      });
    });

    return true;
  };

  getFromPath = (obj, path) => {
    if (!path) return undefined;
    return String(path)
      .split('.')
      .reduce(
        (acc, p) => (acc && acc[p] !== undefined ? acc[p] : undefined),
        obj
      );
  };

  isFieldPopulated = (val) => {
    if (val === undefined || val === null) return false;
    if (typeof val === 'string') return val.trim().length > 0;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'object')
      return Object.values(val).some(
        (v) => v !== '' && v !== null && v !== undefined
      );
    return true;
  };

  maybeSkipIfPopulated = (questionJson, index) => {
    if (!questionJson?.skipIfPopulated) return false;

    const projectedFormValues = this.getProjectedFormValues();
    const fields = [];

    for (const group of questionJson.questions || []) {
      for (const [key, qc] of Object.entries(group)) {
        if (key === 'layout' || key === 'columns') continue;
        fields.push(qc?.name || key);
      }
    }

    if (fields.length === 0) return false;

    const allPopulated = fields.every((fieldName) =>
      this.isFieldPopulated(this.getFromPath(projectedFormValues, fieldName))
    );

    if (!allPopulated) return false;

    const { flowSlug, category, product } = this.getRouteMetaFromProps(
      this.props
    );
    const isGoingBack = this.navigationDirection === 'back';
    const targetIndex = isGoingBack ? index - 1 : index + 1;

    if (isGoingBack && targetIndex < 1) return false;

    if (!isGoingBack && questionJson.gtm) {
      fireGtmSteps(questionJson.gtm, product);
    }

    this.setState({ currentIndex: targetIndex }, () => {
      this.navigateToQuestion({
        flowSlug,
        category,
        product,
        questionIndex: targetIndex,
        replace: true,
      });
    });

    return true;
  };

  maybeSkipComboQuestion = (questionJson, index) => {
    const meta = this.getRouteMetaFromProps(this.props);
    const projectedFormValues = this.getProjectedFormValues();
    const selectedProducts = getSelectedComboProducts(this.state.formValues);
    const forceComboQuestionReview =
      this.props.location?.state?.forceComboQuestionReview === true;

    if (
      meta.product === COMBO_FLOW_PRODUCT &&
      Array.isArray(questionJson?.comboRequiredForProducts) &&
      questionJson.comboRequiredForProducts.length > 0 &&
      !questionJson.comboRequiredForProducts.some((product) =>
        selectedProducts.includes(product)
      )
    ) {
      const nextIndex = index + 1;
      this.setState({ currentIndex: nextIndex }, () => {
        this.navigateToQuestion({
          flowSlug: meta.flowSlug,
          category: meta.category,
          product: meta.product,
          questionIndex: nextIndex,
          replace: true,
        });
      });

      return true;
    }

    if (
      !this.isComboFlowActive(this.props, this.state.formValues) ||
      (meta.product === COMBO_FLOW_PRODUCT && forceComboQuestionReview) ||
      !shouldSkipComboQuestion(questionJson, projectedFormValues)
    ) {
      return false;
    }

    const nextIndex = index + 1;
    this.setState({ currentIndex: nextIndex }, () => {
      this.navigateToQuestion({
        flowSlug: meta.flowSlug,
        category: meta.category,
        product: meta.product,
        questionIndex: nextIndex,
        replace: true,
      });
    });

    return true;
  };

  // ---------- lifecycle ----------
  componentDidMount() {
    const { state } = this.props.location;
    const meta = this.getRouteMetaFromProps(this.props);
    const { category, product, stepIndexParam, flowSlug } = meta;

    const savedFormValues = SessionManager.getFormValues();
    const baseFormValues = state?.formValues || savedFormValues || {};
    const isReturningUserEntry =
      product === 'CarInsurance' &&
      category === 'Vehicle' &&
      (localStorage.getItem('latestVehicleQuotes') || isValidatedWithin());
    const initialFormValues = isReturningUserEntry
      ? this.seedReturningUserListsFromQuote(
          this.seedDriversFromAccountCache(
            this.seedVehiclesFromAccountCache(baseFormValues)
          )
        )
      : baseFormValues;

    // --- Guard: prevent deep-linking directly into later steps ---
    const isDeepLinkToLaterStep =
      !state?.questionIndex && stepIndexParam && stepIndexParam !== 1;

    if (isDeepLinkToLaterStep) {
      // Force redirect to step1
      this.setState(
        {
          currentIndex: 1,
          formValues: initialFormValues,
        },
        () => {
          if (flowSlug) {
            this.props.navigate(`/${flowSlug}/step1`, {
              replace: true,
              state: {
                ...state,
                questionIndex: 1,
                formValues: initialFormValues,
                category,
                product,
              },
            });
          }
        }
      );

      // Returning user entry point (CarInsurance only, step 1)
      if (
        (localStorage.getItem('latestVehicleQuotes') || isValidatedWithin()) &&
        product === 'CarInsurance' &&
        category === 'Vehicle'
      ) {
        setTimeout(() => {
          this.switchFlow('ReturningUser', 1);
        }, 0);
      } else {
        this.loadQuestionData(1);
      }
      window.addEventListener('hippo:jwt-expired', this.handleJwtExpired);
      return;
    }

    const startIndex =
      stepIndexParam || state?.questionIndex || this.state.currentIndex;

    this.setState({
      currentIndex: startIndex,
      formValues: initialFormValues,
    });

    // Returning user entry point (CarInsurance only, step 1)
    if (
      startIndex === 1 &&
      (localStorage.getItem('latestVehicleQuotes') || isValidatedWithin()) &&
      product === 'CarInsurance' &&
      category === 'Vehicle'
    ) {
      setTimeout(() => {
        this.switchFlow('ReturningUser', 1);
      }, 0);
      return;
    }

    this.loadQuestionData(startIndex);
    window.addEventListener('hippo:jwt-expired', this.handleJwtExpired);
  }

  componentWillUnmount() {
    window.removeEventListener('hippo:jwt-expired', this.handleJwtExpired);
  }

  handleJwtExpired = () => {
    if (!ENABLE_OTP_SMS) return;
    const session = safeParse(localStorage.getItem('authSession'), {}) || {};
    const rawAuthVia = session?.otpVia === 'email' ? 'email' : 'phone';
    // SMS is disabled — always fall back to the email flow, even for a
    // session that previously authenticated via phone.
    const authVia = ENABLE_OTP_SMS ? rawAuthVia : 'email';

    const projectedFormValues = this.getProjectedFormValues();
    const firstNameHint =
      projectedFormValues?.FirstName || projectedFormValues?.firstName || '';

    this.setState({
      otpModalOpen: true,
      otpModalData: buildRequestOtpModal(firstNameHint, {
        via: authVia,
        returningUser: true,
      }),
      otpStage: 'request',
      otpViaEmail: authVia === 'email',
    });
  };

  componentDidUpdate(prevProps, prevState) {
    const prevMeta = this.getRouteMetaFromProps(prevProps);
    const currMeta = this.getRouteMetaFromProps(this.props);

    const prevPath = `${prevMeta.flowSlug || ''}/${prevMeta.category || ''}/${
      prevMeta.product || ''
    }`;
    const currPath = `${currMeta.flowSlug || ''}/${currMeta.category || ''}/${
      currMeta.product || ''
    }`;

    // Flow changed (e.g. different product within same slug)
    if (prevPath !== currPath) {
      const { state } = this.props.location;
      const questionIndex =
        currMeta.stepIndexParam || state?.questionIndex || 1;

      this.setState({ currentIndex: questionIndex }, () => {
        this.loadQuestionData(questionIndex);
      });
      return;
    }

    // URL step changed externally (manual typing, history navigation, etc.)
    if (
      prevMeta.stepIndexParam !== currMeta.stepIndexParam &&
      currMeta.stepIndexParam &&
      currMeta.stepIndexParam !== this.state.currentIndex
    ) {
      const { state } = this.props.location;
      const { flowSlug, category, product } = currMeta;

      // Was this navigation done internally by QuestionEngine (we always
      // pass a matching state.questionIndex), or is it a manual/deep link?
      const isInternalNav =
        state?.questionIndex === currMeta.stepIndexParam &&
        state?.questionIndex != null;

      if (!isInternalNav && currMeta.stepIndexParam !== 1) {
        // Manual/deep navigation to later step -> force back to step1
        const savedFormValues =
          SessionManager.getFormValues() || this.state.formValues || {};

        this.setState(
          {
            currentIndex: 1,
            formValues: savedFormValues,
          },
          () => {
            if (flowSlug) {
              this.props.navigate(`/${flowSlug}/step1`, {
                replace: true,
                state: {
                  ...state,
                  questionIndex: 1,
                  formValues: savedFormValues,
                  category,
                  product,
                },
              });
            }
          }
        );
        return;
      }

      // Normal (internal) step navigation
      const nextIndex = currMeta.stepIndexParam;
      this.setState({ currentIndex: nextIndex }, () => {
        this.loadQuestionData(nextIndex);
      });
      return;
    }

    // Same flow, internal step index changed
    if (prevState.currentIndex !== this.state.currentIndex) {
      this.loadQuestionData(this.state.currentIndex);
    }
  }

  // ---------- OTP flow ----------
  openEnterOtpModal = (cell, firstNameHint) => {
    this.setState({
      otpModalOpen: true,
      otpModalData: buildEnterOtpModal(cell, firstNameHint),
      otpStage: 'enter',
    });
  };

  fetchReturnUserDataSilently = async (
    identifierRaw,
    { via = 'phone' } = {}
  ) => {
    try {
      const { product } = this.getRouteMetaFromProps(this.props);
      const isEmail = via === 'email';
      const lookupId = isEmail
        ? String(identifierRaw || '').trim()
        : normalizeCellNumber(identifierRaw);

      getCrossSellQuotes(lookupId)
        .then(persistCrossSellQuotes)
        .catch(() => {});

      const [personRes, vehiclesRes, driversRes, propertiesRes] =
        await Promise.all([
          profileRetrievePerson(lookupId),
          profileRetrieveVehicles(lookupId),
          profileRetrieveVehicleDrivers(lookupId),
          profileRetrievePropertyDetails(lookupId),
        ]);
      const petsRes = await profileRetrievePetDetails(lookupId).catch(
        () => null
      );

      const res = {
        ...personRes,
        Vehicles: vehiclesRes?.Vehicles || [],
        Drivers: driversRes?.Drivers || [],
        Properties: propertiesRes || null,
        Pets: petsRes?.Pets || [],
      };

      const maybeMerged = updateFormValuesFromApi(res);
      const mergedFormValues =
        maybeMerged && typeof maybeMerged === 'object'
          ? maybeMerged
          : SessionManager.getFormValues() || {};

      persistReturnUserDetails(res);

      const resolvedCellNumber = isEmail
        ? normalizeCellNumber(personRes?.CellNumber || '')
        : lookupId;

      void syncAccountData(resolvedCellNumber || lookupId, {
        seedPerson: personRes,
        seedVehicles: vehiclesRes,
        seedDrivers: driversRes,
        seedProperties: propertiesRes,
        seedPets: petsRes,
      }).catch(() => {});
      markOtpVerifiedForProduct(product);

      const nextFormValues = this.isComboFlowActive(
        this.props,
        this.state.formValues
      )
        ? this.mergeIntoCurrentFormValues(
            this.state.formValues,
            mergedFormValues
          )
        : mergedFormValues;

      this.persistFormValues(nextFormValues);
      this.setState({ formValues: nextFormValues });
      return true;
    } catch (err) {
      return false;
    }
  };

  sendOtpTo = async (cellRaw) => {
    const normalized = normalizeCellNumber(cellRaw);

    const projectedFormValues = this.getProjectedFormValues();
    const firstNameHint =
      projectedFormValues?.FirstName || projectedFormValues?.firstName || '';

    await profileSendOTPViaSMS(normalized, firstNameHint);
    persistAuthSession({
      cellNumber: normalized,
      otp: null,
      requestedAt: Date.now(),
    });
    this.openEnterOtpModal(normalized, firstNameHint);
  };

  sendEmailOtpTo = async (email) => {
    const projectedFormValues = this.getProjectedFormValues();
    const firstNameHint =
      projectedFormValues?.FirstName || projectedFormValues?.firstName || '';
    await profileSendOTPViaEmail(email, firstNameHint);
    persistAuthSession({
      email,
      otp: null,
      requestedAt: Date.now(),
    });

    this.setState({
      otpModalOpen: true,
      otpModalData: buildEnterEmailOtpModal(email, firstNameHint),
      otpStage: 'enter',
      otpViaEmail: true,
    });
  };

  tryGetReturnUserDetailsByEmail = async (email, otp) => {
    this.setState({ isLoading: true });
    try {
      const { product } = this.getRouteMetaFromProps(this.props);

      const validateResult = await profileValidateOTP({ email, otp });
      const validatedEntry = Array.isArray(validateResult)
        ? validateResult[0]
        : null;
      if (!validatedEntry?.Validated) {
        throw new Error('OTP validation failed');
      }
      // Store JWT immediately so subsequent profile calls are authenticated
      persistAuthSession({
        jwtToken: validatedEntry.JWTToken,
        jwtValidTo: validatedEntry.ValidToDateUTC,
        otpVia: 'email',
      });

      getCrossSellQuotes(email)
        .then(persistCrossSellQuotes)
        .catch(() => {});

      const [personRes, vehiclesRes, driversRes] = await Promise.all([
        profileRetrievePerson(email),
        profileRetrieveVehicles(email),
        profileRetrieveVehicleDrivers(email),
      ]);
      const petsRes = await profileRetrievePetDetails(email).catch(() => null);

      const res = {
        ...personRes,
        Vehicles: vehiclesRes?.Vehicles || [],
        Drivers: driversRes?.Drivers || [],
        Pets: petsRes?.Pets || [],
      };

      const maybeMerged = updateFormValuesFromApi(res);
      const mergedFormValues =
        maybeMerged && typeof maybeMerged === 'object'
          ? maybeMerged
          : SessionManager.getFormValues() || {};

      persistReturnUserDetails(res);
      persistAuthSession({ email, otp, validatedAt: Date.now() });
      markOtpVerifiedForProduct(product);

      const nextFormValues = this.isComboFlowActive(
        this.props,
        this.state.formValues
      )
        ? this.mergeIntoCurrentFormValues(
            this.state.formValues,
            mergedFormValues
          )
        : mergedFormValues;

      this.persistFormValues(nextFormValues);

      this.setState(
        {
          formValues: nextFormValues,
          otpModalOpen: false,
          otpModalData: null,
          otpStage: 'idle',
          otpViaEmail: false,
        },
        () => this.doNavigateNextOrModal(this.state.questionData)
      );

      this.setState({ isLoading: false });
      return true;
    } catch (err) {
      this.setState((prev) => ({
        otpStage: 'enter',
        otpModalOpen: true,
        otpError: 'Invalid code. Please try again.',
        otpResetCounter: prev.otpResetCounter + 1,
        isLoading: false,
      }));
      return false;
    }
  };

  tryGetReturnUserDetails = async (cellRaw, otp) => {
    this.setState({ isLoading: true });
    try {
      const { product } = this.getRouteMetaFromProps(this.props);
      const normalized = normalizeCellNumber(cellRaw);

      const validateResult = await profileValidateOTP({
        cellNumber: normalized,
        otp,
      });
      const validatedEntry = Array.isArray(validateResult)
        ? validateResult[0]
        : null;
      if (!validatedEntry?.Validated) {
        throw new Error('OTP validation failed');
      }
      // Store JWT immediately so subsequent profile calls are authenticated
      persistAuthSession({
        jwtToken: validatedEntry.JWTToken,
        jwtValidTo: validatedEntry.ValidToDateUTC,
        otpVia: 'sms',
      });

      getCrossSellQuotes(normalized)
        .then(persistCrossSellQuotes)
        .catch(() => {});

      const [personRes, vehiclesRes, driversRes, propertiesRes] =
        await Promise.all([
          profileRetrievePerson(normalized),
          profileRetrieveVehicles(normalized),
          profileRetrieveVehicleDrivers(normalized),
          profileRetrievePropertyDetails(normalized),
        ]);
      const petsRes = await profileRetrievePetDetails(normalized).catch(
        () => null
      );

      const res = {
        ...personRes,
        Vehicles: vehiclesRes?.Vehicles || [],
        Drivers: driversRes?.Drivers || [],
        Properties: propertiesRes || null,
        Pets: petsRes?.Pets || [],
      };

      const maybeMerged = updateFormValuesFromApi(res);
      const mergedFormValues =
        maybeMerged && typeof maybeMerged === 'object'
          ? maybeMerged
          : SessionManager.getFormValues() || {};

      persistReturnUserDetails(res);
      persistAuthSession({
        cellNumber: normalized,
        otp,
        validatedAt: Date.now(),
      });
      void syncAccountData(normalized, {
        seedPerson: personRes,
        seedVehicles: vehiclesRes,
        seedDrivers: driversRes,
        seedProperties: propertiesRes,
        seedPets: petsRes,
      }).catch(() => {});
      markOtpVerifiedForProduct(product);

      const nextFormValues = this.isComboFlowActive(
        this.props,
        this.state.formValues
      )
        ? this.mergeIntoCurrentFormValues(
            this.state.formValues,
            mergedFormValues
          )
        : mergedFormValues;

      this.persistFormValues(nextFormValues);

      this.setState(
        {
          formValues: nextFormValues,
          otpModalOpen: false,
          otpModalData: null,
          otpStage: 'idle',
        },
        () => this.doNavigateNextOrModal(this.state.questionData)
      );

      this.setState({ isLoading: false });
      return true;
    } catch (err) {
      this.setState((prev) => ({
        otpStage: 'enter',
        otpModalOpen: true,
        otpError: 'Invalid code. Please try again.',
        otpResetCounter: prev.otpResetCounter + 1,
        isLoading: false,
      }));
      return false;
    }
  };

  // ---------- question loading ----------
  finishLoadWithQuestionJson = (questionJson, isBackNavigation = false) => {
    if (
      questionJson?.questions?.some((group) =>
        Object.values(group).some((field) => field?.type === 'address-bar')
      )
    ) {
      const projectedFormValues = this.getProjectedFormValues();
      const updatedProjectedValues =
        autoFillMissingAddresses(projectedFormValues);
      const updatedFormValues = this.isComboFlowActive(
        this.props,
        this.state.formValues
      )
        ? this.mergeIntoCurrentFormValues(
            this.state.formValues,
            updatedProjectedValues
          )
        : updatedProjectedValues;

      this.persistFormValues(updatedFormValues);
      this.setState({ formValues: updatedFormValues });
    }

    let questionDataToStore = questionJson;

    if (!isBackNavigation && questionJson.gtm) {
      const { product } = this.getRouteMetaFromProps(this.props);
      const steps = Array.isArray(questionJson.gtm)
        ? questionJson.gtm
        : [questionJson.gtm];
      const onLoadSteps = steps.filter((s) => s.step_name === 'start_quote');
      const onSubmitSteps = steps.filter((s) => s.step_name !== 'start_quote');

      if (onLoadSteps.length > 0) {
        fireGtmSteps(onLoadSteps, product);
        const remainingGtm =
          onSubmitSteps.length === 0
            ? undefined
            : onSubmitSteps.length === 1
              ? onSubmitSteps[0]
              : onSubmitSteps;
        questionDataToStore = { ...questionJson, gtm: remainingGtm };
      }
    }

    this.setState(
      {
        questionData: questionDataToStore,
        isDone: false,
        activeModalData: null,
        showModal: false,
      },
      this.unlockQuestionNavigation
    );
  };

  loadQuestionData = async (index) => {
    try {
      const { category, product } = this.getRouteMetaFromProps(this.props);
      const id = `${category}/${product}`;
      const data = await import(
        `../../json/components/QuestionsEngine/${id}/Question${index}.json`
      );
      const questionJson = data.default;

      if (this.maybeSkipComboQuestion(questionJson, index)) {
        return;
      }

      if (this.maybeSkipIfPopulated(questionJson, index)) {
        return;
      }

      const isBackNavigation = this.navigationDirection === 'back';
      this.navigationDirection = 'forward';
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // ---------- ID-based prefill ----------
      const projectedFormValues = this.getProjectedFormValues();

      if (questionJson.prefillFromID && projectedFormValues?.idNumber) {
        if (
          projectedFormValues.idNumber !==
          localStorage.getItem('idNumberAnchor')
        ) {
          // Preserve the existing OTP session here.
          // Changing IDs should refresh the quote data, but it should not force
          // the user to re-authenticate until the OTP window actually expires.
          localStorage.removeItem('idNumberAnchor');

          this.setState((prev) => {
            const prevForm = this.getProjectedFormValues(prev.formValues);
            const fullName = (prevForm.fullName || '').trim();
            const idNumber = prevForm.idNumber || '';

            const [first = '', ...rest] = fullName.split(' ');
            const last = rest.join(' ');

            // Wipe only the previous person's data - answers that aren't
            // person-derived (e.g. the fibre address) survive the ID change.
            const minimal = {
              ...stripPrefillPersonValues(prevForm),
              idNumber,
              fullName,
              FirstName: first,
              LastName: last,
            };

            const nextFormValues = this.isComboFlowActive(
              this.props,
              prev.formValues
            )
              ? this.mergeIntoCurrentFormValues(prev.formValues, minimal)
              : minimal;

            this.persistFormValues(nextFormValues);
            return { formValues: nextFormValues };
          });
        }

        try {
          this.setState({ isLoading: true });

          const prefillData = await getPersonPreFill(
            projectedFormValues.idNumber
          );

          this.setState({ isLoading: false });

          if (isEmptyPrefill(prefillData)) {
            this.setState((prev) => {
              const prevForm = this.getProjectedFormValues(prev.formValues);
              const fullName = (prevForm.fullName || '').trim();
              const idNumber = prevForm.idNumber || '';

              const [first = '', ...rest] = fullName.split(' ') || [];
              const last = rest.join(' ');

              const minimal = {
                ...stripPrefillPersonValues(prevForm),
                idNumber,
                fullName,
                FirstName: first,
                LastName: last,
              };

              const nextFormValues = this.isComboFlowActive(
                this.props,
                prev.formValues
              )
                ? this.mergeIntoCurrentFormValues(prev.formValues, minimal)
                : minimal;

              this.persistFormValues(nextFormValues);
              return { formValues: nextFormValues };
            });
          } else {
            this.setState((prev) => {
              const currentProjectedValues = this.getProjectedFormValues(
                prev.formValues
              );
              const updatedFormValues = this.mergeIntoCurrentFormValues(
                prev.formValues,
                transformPrefillResponse(prefillData, currentProjectedValues)
              );

              this.persistFormValues(updatedFormValues);
              return { formValues: updatedFormValues };
            });
          }
        } catch (err) {
          // ignore
        }
      }

      localStorage.setItem(
        'idNumberAnchor',
        projectedFormValues.idNumber || ''
      );

      this.finishLoadWithQuestionJson(questionJson, isBackNavigation);
    } catch (err) {
      if (
        this.isComboFlowActive(this.props, this.state.formValues) &&
        this.getRouteMetaFromProps(this.props).flowSlug === COMBO_FLOW_SLUG &&
        this.getRouteMetaFromProps(this.props).product === COMBO_FLOW_PRODUCT
      ) {
        this.startComboJourney();
        return;
      }

      if (
        this.isComboFlowActive(this.props, this.state.formValues) &&
        this.getRouteMetaFromProps(this.props).flowSlug === COMBO_FLOW_SLUG &&
        this.getRouteMetaFromProps(this.props).product !== COMBO_FLOW_PRODUCT &&
        this.advanceComboJourneyToNextFlow()
      ) {
        return;
      }

      this.setState(
        { isDone: true, questionData: null },
        this.unlockQuestionNavigation
      );
    }
  };

  // ---------- navigation ----------
  doNavigateNext = () => {
    const { flowSlug, category, product } = this.getRouteMetaFromProps(
      this.props
    );

    if (this.state.questionData?.gtm) {
      fireGtmSteps(this.state.questionData.gtm, product);
    }

    this.setState(
      (prev) => ({ currentIndex: prev.currentIndex + 1 }),
      () => {
        if (flowSlug) {
          this.navigateToQuestion({
            flowSlug,
            category,
            product,
            questionIndex: this.state.currentIndex,
            replace: false,
          });
        }
      }
    );
  };

  shouldSkipModal = (modal) => {
    if (!modal) return true;
    if (modal.skipIfPopulated) {
      const val = this.getProjectedFormValues()?.[modal.skipIfPopulated];
      // Already opted in — never show again
      if (val === true) return true;
      // Opted out — show at most once (fall through to skipIfShownAlready)
    }
    if (modal.skipIfShownAlready) {
      if (sessionStorage.getItem(`modal_shown_${modal.title}`)) return true;
    }
    return false;
  };

  markModalShown = (modal) => {
    if (modal?.skipIfShownAlready) {
      sessionStorage.setItem(`modal_shown_${modal.title}`, '1');
    }
  };

  doNavigateNextOrModal = (currentQuestion) => {
    if (
      currentQuestion?.modal &&
      !this.shouldSkipModal(currentQuestion.modal)
    ) {
      this.setState({
        showModal: true,
        activeModalData: currentQuestion.modal,
      });
    } else {
      this.doNavigateNext();
    }
  };

  handleNext = async () => {
    if (this.questionNavigationLocked || this.state.isLoading) return;
    this.lockQuestionNavigation();

    const currentQuestion = this.state.questionData;

    // ---------- OTP step-up (fires on submit of the question that collected the cell number) ----------
    if (currentQuestion?.otpStepup === true) {
      const form = this.getProjectedFormValues();
      const authSession = safeParse(localStorage.getItem('authSession'), {});
      const isEmailAuth = authSession?.otpVia === 'email';
      const cellFromForm =
        form?.cellNumber || form?.phone || form?.CellNumber || '';
      const cell = normalizeCellNumber(
        authSession?.cellNumber || cellFromForm || ''
      );

      // Re-fetch using whichever identifier the current JWT was actually
      // issued against — mixing channels here causes the backend to 401
      // (the JWT carries a ValidatedViaEmail flag), which trips the global
      // hippo:jwt-expired listeners and pops a duplicate "Is that you?" modal.
      if (isValidatedWithin()) {
        const identifier = isEmailAuth ? authSession?.email || '' : cell;
        if (identifier) {
          this.fetchReturnUserDataSilently(identifier, {
            via: isEmailAuth ? 'email' : 'phone',
          });
          this.doNavigateNextOrModal(currentQuestion);
          return;
        }
      }

      // Match the marketing modal's session-based suppression: once the OTP
      // verification modal has been shown during the question flow, don't
      // re-show it (or re-send an OTP) for the rest of the session — just
      // continue. The homepage returning-user and sign-in flows are unaffected.
      if (wasOtpStepupShown()) {
        this.doNavigateNextOrModal(currentQuestion);
        return;
      }

      // OTP is disabled entirely — skip verification and continue.
      if (!ENABLE_OTP_SMS) {
        this.doNavigateNextOrModal(currentQuestion);
        return;
      }

      if (cell) {
        const firstNameHint = form?.FirstName || form?.firstName || '';
        // Ask before sending — the SMS itself only fires once the user
        // clicks "Send OTP" on this modal (see handleOtpModalAction).
        this.setState({
          otpModalOpen: true,
          otpModalData: buildConfirmSendOtpModal(cell, firstNameHint),
          otpStage: 'confirm',
          otpViaEmail: false,
        });
        // The verification modal is now on screen — remember it for this
        // session so subsequent quotes don't re-prompt.
        markOtpStepupShown();
        this.unlockQuestionNavigation();
        return;
      }

      this.doNavigateNextOrModal(currentQuestion);
      return;
    }

    // ---------- Marketing modal (fires on submit if configured) ----------
    if (
      currentQuestion?.modal &&
      !this.shouldSkipModal(currentQuestion.modal)
    ) {
      this.setState({
        showModal: true,
        activeModalData: currentQuestion.modal,
      });
      return;
    }

    this.doNavigateNext();
  };

  handleBack = () => {
    this.navigationDirection = 'back';
    const { currentIndex, historyStack } = this.state;
    const { flowSlug, category, product } = this.getRouteMetaFromProps(
      this.props
    );

    if (currentIndex > 1) {
      const nextIndex = currentIndex - 1;
      this.setState({ currentIndex: nextIndex }, () => {
        if (flowSlug) {
          this.navigateToQuestion({
            flowSlug,
            category,
            product,
            questionIndex: nextIndex,
            replace: false,
          });
        }
      });
    } else if (historyStack.length > 0 && flowSlug) {
      const lastState = historyStack[historyStack.length - 1];
      this.setState(
        (prev) => ({
          historyStack: prev.historyStack.slice(0, -1),
        }),
        () => {
          this.navigateToQuestion({
            flowSlug,
            category: lastState.category || category,
            product: lastState.product,
            questionIndex: lastState.questionIndex,
            replace: false,
            formValues: lastState.formValues,
          });
        }
      );
    }
  };

  handleReset = () => {
    const { flowSlug, category, product } = this.getRouteMetaFromProps(
      this.props
    );

    this.setState({ currentIndex: 1, formValues: {} }, () => {
      if (flowSlug) {
        this.navigateToQuestion({
          flowSlug,
          category,
          product,
          questionIndex: 1,
          replace: false,
          formValues: {},
        });
      }
    });
  };

  handleGetQuotesAndNavigateToQuotes = () => {
    const { category, product, flowSlug } = this.getRouteMetaFromProps(
      this.props
    );
    //console.debug('Navigating to quotes for', category, product);
    const path =
      flowSlug === COMBO_FLOW_SLUG
        ? COMBO_RESULTS_PATH
        : getResultsPathForFlow(category, product);

    // Submitting the flow is a deliberate re-quote: clear the chosen flag so
    // the results page renders instead of redirecting to the landing page.
    unmarkProductChosenForQuestionFlow(product);

    this.props.navigate(path, {
      state: {
        formValues: this.state.formValues,
        category: flowSlug === COMBO_FLOW_SLUG ? COMBO_FLOW_CATEGORY : category,
        product: flowSlug === COMBO_FLOW_SLUG ? COMBO_FLOW_PRODUCT : product,
      },
    });
  };

  handleFormChange = (name, value) => {
    this.setState((prevState) => {
      const updatedFormValues = this.applyCurrentFormChange(
        prevState.formValues,
        name,
        value
      );

      this.persistFormValues(updatedFormValues);

      return { formValues: updatedFormValues };
    });
  };

  // Switch underlying product/flow, but keep the same slug routing
  switchFlow = (newProduct, newIndex, options = {}) => {
    const {
      category: currentCategory,
      product,
      flowSlug,
    } = this.getRouteMetaFromProps(this.props);

    if (this.state.questionData?.gtm) {
      fireGtmSteps(this.state.questionData.gtm, product);
    }
    const nextCategory = options.category || currentCategory;

    this.persistFormValues(this.state.formValues);

    const baseState = {
      fromBranching: true,
      questionIndex: newIndex,
      formValues: this.state.formValues,
      category: nextCategory,
      product: newProduct,
      forceComboQuestionReview: options.forceComboQuestionReview === true,
    };

    const replace =
      options.replace ??
      (newProduct === 'ReturningUser' && this.state.currentIndex === 1);

    this.setState(
      (prev) => ({
        historyStack:
          options.pushHistory === false
            ? prev.historyStack
            : [
                ...prev.historyStack,
                {
                  category: currentCategory,
                  product,
                  questionIndex: this.state.currentIndex,
                  formValues: this.state.formValues,
                },
              ],
      }),
      () => {
        if (flowSlug) {
          this.props.navigate(`/${flowSlug}/step${newIndex}`, {
            replace,
            state: baseState,
          });
        }
      }
    );
  };

  // ---------- marketing modal handlers ----------
  handleModalClose = (actionId) => {
    if (actionId === 'yes-send-me-deals' || actionId === 'no-just-quote-me') {
      this.markModalShown(this.state.activeModalData);
      this.setState((prev) => {
        const updatedFormValues = this.applyCurrentFormChange(
          prev.formValues,
          'hippoMarketingOptIn',
          actionId === 'yes-send-me-deals'
        );

        this.persistFormValues(updatedFormValues);
        return { formValues: updatedFormValues };
      });
    }
    this.setState({ showModal: false, activeModalData: null });
    this.doNavigateNext();
  };

  // ---------- OTP modal handlers ----------
  handleOtpModalClose = () => {
    this.setState({ otpModalOpen: false });
  };

  handleOtpModalAction = async (id, formState) => {
    if (id === 'send-otp') {
      if (this.state.otpSubmitting) return;
      if (this.state.otpViaEmail) {
        const enteredEmail = String(formState?.email_address || '').trim();
        if (!enteredEmail) return;
        this.setState({ otpSubmitting: true });
        try {
          await this.sendEmailOtpTo(enteredEmail);
        } finally {
          this.setState({ otpSubmitting: false });
        }
        return;
      }
      const form = this.getProjectedFormValues();
      const authSession = safeParse(localStorage.getItem('authSession'), {});
      const enteredCell =
        formState?.contact_number ||
        authSession?.cellNumber ||
        form?.cellNumber ||
        form?.phone ||
        form?.CellNumber ||
        '';
      if (!String(enteredCell || '').trim()) return;
      if (!isValidSaContactNumber(enteredCell)) return;
      this.setState({ otpSubmitting: true });
      try {
        await this.sendOtpTo(enteredCell);
      } finally {
        this.setState({ otpSubmitting: false });
      }
      return;
    }

    if (id === 'use-email-instead') {
      const form = this.getProjectedFormValues();
      const email = form?.email || form?.EmailAddress || '';
      if (email) {
        this.setState({ otpSubmitting: true });
        try {
          await this.sendEmailOtpTo(email);
        } finally {
          this.setState({ otpSubmitting: false });
        }
        return;
      }
      // No email available — fall through to bypass
    }

    if (id === 'no-thats-not-me') {
      clearPersistedAuthSession();
      localStorage.clear();
      sessionStorage.clear();
      persistAuthSession({ bypassedAt: Date.now() });
      this.setState(
        {
          otpModalOpen: false,
          otpStage: 'idle',
          otpViaEmail: false,
          formValues: {},
        },
        () => this.doNavigateNextOrModal(this.state.questionData)
      );
      return;
    }

    if (
      id === 'first-time-quoting' ||
      id === 'use-email-instead' ||
      id === 'guest-quote'
    ) {
      persistAuthSession({ bypassedAt: Date.now() });
      this.setState(
        { otpModalOpen: false, otpStage: 'idle', otpViaEmail: false },
        () => this.doNavigateNextOrModal(this.state.questionData)
      );
      return;
    }
  };

  handleOtpModalComplete = async (formState) => {
    if (this.state.otpStage !== 'enter') return;
    const otp = (formState?.otp_code ?? '').toString().trim();
    if (!/^\d{4}$/.test(otp)) return;
    if (this.state.otpSubmitting) return;

    const { product } = this.getRouteMetaFromProps(this.props);

    if (this.state.otpViaEmail) {
      const session = safeParse(localStorage.getItem('authSession'), {}) || {};
      const form = this.getProjectedFormValues();
      const email = session?.email || form?.email || form?.EmailAddress || '';
      if (!email) return;

      this.setState({ otpError: '' });
      try {
        this.setState({ otpSubmitting: true });
        const ok = await this.tryGetReturnUserDetailsByEmail(email, otp);
        if (ok) {
          persistAuthSession({ email, otp });
          markOtpVerifiedForProduct(product);
          this.setState({
            otpModalOpen: false,
            otpStage: 'idle',
            otpViaEmail: false,
          });
        }
      } finally {
        this.setState({ otpSubmitting: false });
      }
      return;
    }

    const session = safeParse(localStorage.getItem('authSession'), {}) || {};
    const form = this.getProjectedFormValues();
    const fallback = form?.cellNumber || form?.phone || form?.CellNumber || '';
    const cell =
      session?.cellNumber ||
      fallback ||
      (formState?.contact_number || '').replace(/\D/g, '');

    if (!cell) return;

    this.setState({ otpError: '' });
    try {
      this.setState({ otpSubmitting: true });
      const ok = await this.tryGetReturnUserDetails(cell, otp);
      if (ok) {
        persistAuthSession({
          cellNumber: normalizeCellNumber(cell),
          otp,
        });
        markOtpVerifiedForProduct(product);
        this.setState({ otpModalOpen: false, otpStage: 'idle' });
      }
    } finally {
      this.setState({ otpSubmitting: false });
    }
  };

  render() {
    const {
      questionData,
      isDone,
      currentIndex,
      showModal,
      activeModalData,
      otpModalOpen,
      otpModalData,
      isLoading,
    } = this.state;

    const { isMobile } = this.props;
    const { product, category } = this.getRouteMetaFromProps(this.props);
    const comboModeActive = this.isComboFlowActive(
      this.props,
      this.state.formValues
    );
    const flowKey = comboModeActive ? COMBO_FLOW_PRODUCT : product;
    const currentFormValues = this.getProjectedFormValues();
    const stepperFormValues = comboModeActive
      ? this.state.formValues
      : currentFormValues;
    const stepperOptions = comboModeActive
      ? {
          activeProduct: product,
          activeCategory: category,
        }
      : {};

    return (
      <>
        {SeoTagsManager.render(product)}
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            mt: 0,
            px: 0,
            maxWidth: '1440px',
            width: '100%',
            overflowX: 'hidden',
            overflowY: 'visible',
          }}
        >
          {isDone
            ? this.handleGetQuotesAndNavigateToQuotes()
            : questionData && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      md: '1fr minmax(0, 750px) 270px',
                    },
                    columnGap: { md: 4 },
                    alignItems: 'start',
                    width: '100%',
                    position: 'relative',
                    px: isMobile ? 2 : 4,
                    overflowX: 'hidden',
                    overflowY: 'visible',
                    '& > *': { minWidth: 0 },
                  }}
                >
                  {/* Main question column */}
                  <Box
                    sx={{
                      gridColumn: { xs: '1', md: '2' },
                      justifySelf: 'center',
                      width: '100%',
                      maxWidth: { xs: '100%', md: '750px' },
                      zIndex: 1,
                      overflowX: 'hidden',
                      overflowY: 'visible',
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        overflowX: 'hidden',
                      }}
                    >
                      <QuestionEngineComponent
                        prop={questionData}
                        usePeekABooAvatar={
                          product !== 'ReturningUser' && currentIndex === 1
                        }
                        onNext={this.handleNext}
                        onBack={this.handleBack}
                        switchFlow={this.switchFlow}
                        canGoBack={
                          currentIndex > 1 ||
                          questionData?.showBackButton === true
                        }
                        values={currentFormValues}
                        onChange={this.handleFormChange}
                        isMobile={this.props.isMobile}
                        isLoading={isLoading}
                        formStepper={getStepperData(
                          flowKey,
                          stepperFormValues,
                          questionData?.stepper?.activeStep,
                          stepperOptions
                        )}
                      />
                    </div>
                  </Box>

                  {/* Sticky sidebar (desktop only) */}
                  {!isMobile && (
                    <Box
                      sx={{
                        position: 'fixed',
                        top: 72,
                        left: 'calc(50% + 400px)',
                        width: '270px',
                        zIndex: 1000,
                      }}
                    >
                      <SideProgressBarComponent
                        data={{
                          ...getStepperData(
                            flowKey,
                            stepperFormValues,
                            questionData?.stepper?.activeStep,
                            stepperOptions
                          ),
                          switchFlow: this.switchFlow,
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

          {activeModalData && (
            <PopupModalComponent
              data={activeModalData}
              open={showModal}
              onClose={this.handleModalClose}
              onAction={this.handleModalClose}
              isMobile={this.props.isMobile}
            />
          )}

          {/* OTP Modals */}
          {otpModalOpen && otpModalData && (
            <PopupModalComponent
              data={otpModalData}
              open={otpModalOpen}
              onClose={this.handleOtpModalClose}
              onAction={this.handleOtpModalAction}
              onComplete={this.handleOtpModalComplete}
              isMobile={this.props.isMobile}
              actionBehavior={{ closeOnAction: false, completeOnAction: false }}
              closeOnComplete={false}
              loading={this.state.otpSubmitting}
              otpError={this.state.otpError}
              otpResetCounter={this.state.otpResetCounter}
            />
          )}
        </Container>
      </>
    );
  }
}

const QuestionEngineWithMobile = (props) => {
  const { isMobile } = useMobileContext();
  return <QuestionEngine {...props} isMobile={isMobile} />;
};

export default withRouter(QuestionEngineWithMobile);
